import { useState } from "react";
import QuickForm from "components/Meta/QuickForm";
import StdButton from "components/Buttons/StdButton";
import Kind from "./Kind";
import Shop from "./Shop";
import Source from "./Source";
import InvoiceType from "./InvoiceType";
import ScrollToTop from "./ScrollToTop";
import Consumer from "./Consumer";
import useMainStore from "store/mainStore";
import proxy from "api/proxy";
import actionButton from "store/actionButton";
import ModalMsg from "components/Modals/ModalMsg";
import dates from "tools/dates";
import Timer from "./Timer";
import Divider from "components/MeForm/Divider";
import { useStoreRest } from "apps/RestOrder/storeRest";

function BtnConsumer({ onClick }) {
  return (
    <div>
      <Divider text={"sale_call_center.btn_consumer"} key={"btn_consumer"} />

      <div className="flex justify-center pb-2">
        <StdButton
          color={"blue"}
          size={"w-3/4 h-16"}
          content={"sale.sale.add_consumer"}
          onClick={() => onClick("modalConsumer")}
        />
      </div>
    </div>
  );
}

const STEPS = {
  source: Source,
  kind: Kind,
  btn_consumer: BtnConsumer,
  shop: Shop,
  invoice_type: InvoiceType,
};

function CreateOrder({ ctxView }) {
  const { store, record, updateRecord, updateStore } = useMainStore();
  const { initShop, setIsFilterByShop } = useStoreRest();

  const [openConsumer, setOpenConsumer] = useState(false);
  const [startTimer, setStartTimer] = useState(null);
  const stopTimer = startTimer && Object.keys(store).length <= 0;

  const closeConsumer = async (consumer) => {
    if (consumer) {
      await updateForm("consumer", consumer);
    }
    setOpenConsumer(false);
  };

  async function updateForm(field, value) {
    if (field === "modalConsumer") {
      setOpenConsumer(true);
      return;
    }
    if (value === undefined) return;
    const toStore = { ...store };
    const toRecord = { ...record };

    if (!startTimer) {
      toStore["created"] = dates.getNow();
      setStartTimer(toStore["created"]);
    }

    if (field === "shop") {
      await initShop(value.id);
      await setIsFilterByShop();
      if (!record.party) {
        toStore["party"] = value["party."];
        toRecord["party"] = value["party."];
      }
      if (record?.shop?.id !== value.id) {
        toStore["table_assigned"] = null;
        toRecord["table_assigned"] = null;

        const hasPos = !!value?.pos_authorization;
        const hasElectronic = !!value?.electronic_authorization;

        let invoiceType;
        if (hasPos && hasElectronic) {
          invoiceType = null;
        } else if (hasPos) {
          invoiceType = "P";
        } else if (hasElectronic) {
          invoiceType = "1";
        } else {
          invoiceType = "1";
        }
        toStore["invoice_type"] = invoiceType;
        toRecord["invoice_type"] = invoiceType;
      }
    }

    toStore[field] = value;
    toRecord[field] = value;
    updateRecord(toRecord);
    updateStore(toStore);
  }

  function getSteps() {
    const steps = Object.keys(STEPS).map((step_) => {
      const Component = STEPS[step_];
      return (
        <Component
          key={step_}
          onClick={updateForm}
          record={step_ === "invoice_type" ? record : record[step_]}
          section={step_}
        />
      );
    });
    return steps;
  }

  const [modalProps, setModalProps] = useState({ open: false });
  async function handleButton(btnName) {
    const res =
      (await actionButton({
        button: ctxView.webfields[btnName],
        level: "main",
        model: ctxView.model,
        ctxView,
      })) ?? {};

    const sale = res.data?.record;
    if (btnName === "confirm" && sale?.id > 0 && sale.invoice_type === "M") {
      const { error } = await proxy.save({
        model: "sale.sale",
        ids: [sale.id],
        values: { invoice_method: "manual" },
      });
      if (error) console.error("invoice_method:", error);
    }

    const { msg, data, error, button } = res;
    if (msg) {
      setModalProps({ open: true, msg, type: "error", reset: false });
    } else if (error) {
      const msgError = error instanceof TypeError ? error.message : error.error;
      setModalProps({ open: true, msg: msgError, type: "error", reset: false });
    } else if (data?.msg) {
      setModalProps({
        open: true,
        msg: data.msg,
        type: data.type,
        origin: button,
        reset: true,
      });
    }
  }

  const closeModal = () => setModalProps({ open: false });

  return (
    <div className="mt-3">
      {getSteps()}
      <hr className="h-px m-2 bg-gray-200 border dark:bg-gray-700" />
      <QuickForm ctxView={ctxView} level="main" handleButton={handleButton} />
      {modalProps.open && (
        <ModalMsg
          {...modalProps}
          buttons={["close"]}
          onClose={closeModal}
          onOK={closeModal}
          onDiscard={closeModal}
        />
      )}
      <Consumer
        open={openConsumer}
        consumerId={record?.consumer?.id ?? null}
        closeConsumer={closeConsumer}
      />
      <Timer
        start={startTimer}
        stop={stopTimer}
        handleTimer={() => setStartTimer(null)}
      />
      <ScrollToTop />
    </div>
  );
}

export default CreateOrder;
